import { TimeRuleAlert, WorkRulesInfo } from './TimeRuleAlert';
// Step 1: Temel Bilgiler - adım adım yönlendirmeli
import { BehalfFirmPicker } from './BehalfFirmPicker';
import { EquipmentQuestion, equipmentAnswerDone, useEquipmentKinds } from './EquipmentQuestion';
import { SpecialSituationsQuestion } from './SpecialSituationsQuestion';
import { StorePicker } from './StorePicker';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Checkbox } from '../../ui/checkbox';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { toast } from 'sonner';
import { CheckCircle, Check } from 'lucide-react';
import { WORK_PERMIT_TYPES } from '../constants';
import { CorrectionGuard, CorrectionHint, unmatchedKeys } from './infoCorrections';
import { useFieldEdit, EditControls, useDocChangeConfirm } from './fieldEdit';

const GuidedField = ({ id, number, label, hint, done, active, urgent, optional, action, children }) => (
  <div
    id={`guided-${id}`}
    data-testid={`guided-field-${id}`}
    data-guide-required={!optional && !done && (active || urgent) ? 'true' : undefined}
    className={`flex gap-3 sm:gap-4 rounded-lg p-3 sm:p-4 border transition-[border-color,background-color,box-shadow] duration-300 animate-in fade-in slide-in-from-top-2 ${urgent ? 'border-rose-400 bg-rose-50/60 shadow-sm' : active ? 'border-emerald-400 bg-emerald-50/50 shadow-sm' : 'border-transparent'}`}
  >
    <div className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 text-sm font-semibold transition-colors ${done ? 'bg-emerald-600 text-white' : urgent ? 'bg-white text-rose-600 ring-2 ring-rose-500' : active ? 'bg-white text-emerald-700 ring-2 ring-emerald-500' : 'bg-slate-100 text-slate-500'}`}>
      {done ? <Check className="h-4 w-4" /> : number}
    </div>
    <div className="flex-1 space-y-2 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-slate-800">
          {label} {optional ? <span className="text-xs font-normal text-slate-400">(isteğe bağlı)</span> : <span className="text-rose-500">*</span>}
        </p>
        {action}
      </div>
      {(active || urgent) && hint && <p className={`text-xs ${urgent ? 'text-rose-600 font-medium' : 'text-emerald-700'}`} data-testid={`guided-hint-${id}`}>{hint}</p>}
      {children}
    </div>
  </div>
);

const SaveButton = ({ saved, onClick, testId, disabled }) => (
  <Button
    type="button"
    size="sm"
    onClick={onClick}
    disabled={disabled}
    className={`h-10 px-4 ${saved ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-rose-600 hover:bg-rose-700 text-white'}`}
    data-testid={testId}
  >
    {saved ? <><Check className="h-4 w-4 mr-1" />Kaydedildi</> : 'Kaydet'}
  </Button>
);

const EDIT_HINT = 'Değişiklik yaptıysanız "Kaydet" butonuna basın; vazgeçmek için X ile kapatın';

const WizardStep1 = ({
  formData,
  setFormData,
  locations,
  companies,
  isTipiOptions,
  isCesidiOptions,
  areasLoading,
  selectedWorkArea,
  setNewCompanyDialog,
  onGuideComplete,
  corrections = null,
  warnDocChange = false,
  guardRemoveKinds,
  timeCheck = null,
}) => {
  const fix = (key, children) => (
    <CorrectionGuard fieldKey={key} correction={corrections?.[key]} formData={formData}>
      {children}
      <CorrectionHint fieldKey={key} correction={corrections?.[key]} formData={formData} locations={locations} />
    </CorrectionGuard>
  );
  const pendingCount = unmatchedKeys(corrections, formData).length;
  const set = (patch) => setFormData(prev => ({ ...prev, ...patch }));
  // Düzenleme modunda saatler zaten girilmiş kabul edilir
  const [startTimeSet, setStartTimeSet] = useState(!!(formData.start_date && formData.start_time));
  const [endTimeSet, setEndTimeSet] = useState(!!(formData.end_date && formData.end_time));
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const [descSet, setDescSet] = useState(!!formData.job_description);
  const edit = useFieldEdit(formData, setFormData, { start: startTimeSet, end: endTimeSet, details: descSet });
  const docChange = useDocChangeConfirm(warnDocChange);
  const editing = (key) => edit.editKey === key;

  const [behalfOpen, setBehalfOpen] = useState(!!formData.on_behalf_of);
  // Firma kendi adına talep oluşturur: firma bilgisi otomatik gelir
  useEffect(() => {
    if (!formData.company_id && companies.length > 0) set({ company_id: companies[0].id });
  }, [companies, formData.company_id]);

  const hasLocation = !!formData.location_id;
  const hasCompany = hasLocation && !!formData.company_id && (!behalfOpen || !!formData.behalf_company_id);
  const needsTipi = areasLoading || isTipiOptions.length > 0;
  const hasTipi = !needsTipi || !!formData.is_tipi;
  const needsStore = !!selectedWorkArea?.store_list;
  const hasCesidi = (!needsTipi || !!formData.is_cesidi) && (!needsStore || !!formData.store_id);
  const off = (needsTipi ? 2 : 0) + (needsStore ? 1 : 0);
  const hasStart = hasCesidi && !!formData.start_date;
  const hasStartTime = hasStart && startTimeSet;
  const hasEnd = !!formData.end_date && hasStartTime;
  const hasEndTime = hasEnd && endTimeSet;
  const hasDesc = hasEndTime && descSet && !!formData.job_description?.trim();
  const activeKey = !hasLocation ? 'location' : !hasCompany ? 'company' : !hasTipi ? 'tipi' : !(!needsTipi || formData.is_cesidi) ? 'cesidi' : !hasCesidi ? 'store'
    : !hasStartTime ? 'start' : !hasEndTime ? 'end' : !hasDesc ? 'details' : 'types';

  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    document.getElementById(`guided-${activeKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [activeKey]);

  const eqKinds = useEquipmentKinds();
  const eqDone = equipmentAnswerDone(formData, eqKinds);
  const [ssDone, setSsDone] = useState(false);
  useEffect(() => { onGuideComplete?.(hasDesc && !edit.dirty && eqDone && ssDone); }, [hasDesc, edit.dirty, eqDone, ssDone]);

  // Saat "Kaydet" butonuna (veya Enter'a) basınca tamamlanmış sayılır
  const confirmOnKey = (setter, key) => (e) => { if (e.key === 'Enter') { e.preventDefault(); if (editing(key)) edit.save(); else setter(true); e.currentTarget.blur(); } };

  return (
    <div className="space-y-2">
      {docChange.dialog}
      {corrections && Object.keys(corrections).length > 0 && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${pendingCount ? 'border-rose-300 bg-rose-50 text-rose-700' : 'border-emerald-300 bg-emerald-50 text-emerald-800'}`} data-testid="info-corrections-banner">
          {pendingCount
            ? <>İSG Uzmanı talep bilgilerinde hata buldu. Kırmızı işaretli <b>{pendingCount} alanı</b> önerilen bilgiyle aynı şekilde güncelleyin.</>
            : <>Tüm önerilen bilgiler girildi. Bölümlere devam edip talebi tekrar İSG incelemesine gönderebilirsiniz.</>}
        </div>
      )}
      <GuidedField id="location" number={1} label="Lokasyon" hint="Önce çalışmanın yapılacağı lokasyonu seçin" done={hasLocation} active={activeKey === 'location'}>
        {fix('location', (
        <Select value={formData.location_id} onValueChange={(val) => val !== formData.location_id && docChange.guard(() => set({ location_id: val, is_tipi: '', is_cesidi: '', store_id: '' }))}>
          <SelectTrigger data-testid="wizard-location-select"><SelectValue placeholder="Lokasyon seçin" /></SelectTrigger>
          <SelectContent>
            {locations.map(loc => <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>)}
          </SelectContent>
        </Select>
        ))}
      </GuidedField>

      {hasLocation && (
        <GuidedField
          id="company" number={2} label="Firma" hint="Talep kendi firmanız adına oluşturulur" done={hasCompany} active={activeKey === 'company'}
        >
          <div className="space-y-3">
            <div className="h-10 px-3 flex items-center rounded-md border bg-slate-50 text-sm font-medium text-slate-800" data-testid="wizard-company-name">
              {companies.find(c => c.id === formData.company_id)?.name || companies[0]?.name || '-'}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <Checkbox
                checked={behalfOpen}
                onCheckedChange={(v) => { setBehalfOpen(!!v); if (!v) set({ on_behalf_of: '', behalf_company_id: '' }); }}
                data-testid="wizard-behalf-checkbox"
              />
              Başka firma adına talep oluştur
            </label>
            {fix('on_behalf_of', behalfOpen && (
              <BehalfFirmPicker name={formData.on_behalf_of} behalfId={formData.behalf_company_id}
                onPick={(f) => set({ on_behalf_of: f?.name || '', behalf_company_id: f?.id || '' })} />
            ))}
          </div>
        </GuidedField>
      )}

      {hasCompany && needsTipi && (
        <GuidedField id="tipi" number={3} label="İş Tipi" hint="Yapılacak işin tipini seçin" done={!!formData.is_tipi} active={activeKey === 'tipi'}>
          <Select value={formData.is_tipi} onValueChange={(val) => val !== formData.is_tipi && docChange.guard(() => set({ is_tipi: val, is_cesidi: '' }))}>
            <SelectTrigger data-testid="wizard-is-tipi-select"><SelectValue placeholder="İş tipi seçin" /></SelectTrigger>
            <SelectContent>
              {isTipiOptions.map(tip => <SelectItem key={tip} value={tip}>{tip}</SelectItem>)}
            </SelectContent>
          </Select>
        </GuidedField>
      )}

      {hasCompany && needsTipi && hasTipi && (
        <GuidedField id="cesidi" number={4} label="İş Çeşidi" hint="İşin çeşidini seçin" done={!!formData.is_cesidi} active={activeKey === 'cesidi'}>
          <Select value={formData.is_cesidi} onValueChange={(val) => val !== formData.is_cesidi && docChange.guard(() => set({ is_cesidi: val }))}>
            <SelectTrigger data-testid="wizard-is-cesidi-select"><SelectValue placeholder="İş çeşidi seçin" /></SelectTrigger>
            <SelectContent>
              {isCesidiOptions.map(cesit => <SelectItem key={cesit} value={cesit}>{cesit}</SelectItem>)}
            </SelectContent>
          </Select>
          {selectedWorkArea && (
            <p className="text-sm text-emerald-700"><CheckCircle className="h-4 w-4 inline mr-1" />Çalışma alanı eşleşti</p>
          )}
          {fix('job_type', null)}
        </GuidedField>
      )}

      {hasCompany && needsStore && !!formData.is_cesidi && (
        <GuidedField id="store" number={5} label="Çalışılacak Mağaza/İşletme" hint="Çalışmanın yapılacağı mağaza/işletmeyi seçin; talep onaylanınca mağazaya bilgi gider" done={!!formData.store_id} active={activeKey === 'store'}>
          <StorePicker locationId={formData.location_id} value={formData.store_id} onPick={(st) => set({ store_id: st.id })} />
        </GuidedField>
      )}

      {hasCompany && hasCesidi && <WorkRulesInfo rules={timeCheck?.work_rules} />}
      {hasCompany && hasCesidi && (
        <GuidedField
          id="start" number={3 + off} label="Başlangıç Tarihi ve Saati"
          hint={editing('start') ? EDIT_HINT : hasStart ? 'Şimdi başlangıç saatini seçin ve "Kaydet" butonuna basın' : 'Çalışmanın başlayacağı tarihi girin'}
          done={hasStartTime && !(editing('start') && edit.dirty)} active={activeKey === 'start'} urgent={(hasStart && !hasStartTime) || editing('start')}
        >
          {fix('start', <div className="grid grid-cols-2 sm:grid-cols-[1fr_auto_auto] gap-2 sm:gap-3 [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1" onFocusCapture={() => edit.begin('start')}>
            <Input
              type="date"
              value={formData.start_date}
              data-testid="wizard-start-date"
              onChange={(e) => {
                const v = e.target.value;
                setFormData(prev => (prev.end_date && prev.end_date < v ? { ...prev, start_date: v, end_date: v } : { ...prev, start_date: v }));
                if (v && !startTimeSet) setTimeout(() => startTimeRef.current?.focus(), 50);
              }}
            />
            <Input
              ref={startTimeRef}
              type="time"
              value={formData.start_time}
              onChange={(e) => set({ start_time: e.target.value })}
              onKeyDown={confirmOnKey(setStartTimeSet, 'start')}
              className={`w-[6rem] md:w-[5rem] px-2 ${hasStart && !hasStartTime ? 'border-rose-500 ring-2 ring-rose-300' : ''}`}
              data-testid="wizard-start-time"
            />
            {editing('start') ? <EditControls fieldKey="start" edit={edit} testId="wizard-start" /> : <SaveButton saved={hasStartTime} disabled={!hasStart} onClick={() => setStartTimeSet(true)} testId="wizard-start-time-save" />}
          </div>)}
        </GuidedField>
      )}

      {hasStartTime && (
        <GuidedField
          id="end" number={4 + off} label="Bitiş Tarihi ve Saati"
          hint={editing('end') ? EDIT_HINT : hasEnd ? 'Şimdi bitiş saatini seçin ve "Kaydet" butonuna basın' : 'Çalışmanın biteceği tarihi girin (başlangıçtan önce olamaz)'}
          done={hasEndTime && !(editing('end') && edit.dirty)} active={activeKey === 'end'} urgent={(hasEnd && !hasEndTime) || editing('end')}
        >
          {fix('end', <div className="grid grid-cols-2 sm:grid-cols-[1fr_auto_auto] gap-2 sm:gap-3 [&>*:last-child]:col-span-2 sm:[&>*:last-child]:col-span-1" onFocusCapture={() => edit.begin('end')}>
            <Input
              type="date"
              value={formData.end_date}
              min={formData.start_date || undefined}
              data-testid="wizard-end-date"
              onChange={(e) => {
                const v = e.target.value;
                if (formData.start_date && v < formData.start_date) {
                  toast.error('Bitiş tarihi başlangıç tarihinden önce olamaz!');
                  return;
                }
                set({ end_date: v });
                if (v && !endTimeSet) setTimeout(() => endTimeRef.current?.focus(), 50);
              }}
            />
            <Input
              ref={endTimeRef}
              type="time"
              value={formData.end_time}
              onChange={(e) => set({ end_time: e.target.value })}
              onKeyDown={confirmOnKey(setEndTimeSet, 'end')}
              className={`w-[6rem] md:w-[5rem] px-2 ${hasEnd && !hasEndTime ? 'border-rose-500 ring-2 ring-rose-300' : ''}`}
              data-testid="wizard-end-time"
            />
            {editing('end') ? <EditControls fieldKey="end" edit={edit} testId="wizard-end" /> : <SaveButton saved={hasEndTime} disabled={!hasEnd} onClick={() => setEndTimeSet(true)} testId="wizard-end-time-save" />}
          </div>)}
        </GuidedField>
      )}

      {hasEndTime && (
        <GuidedField id="details" number={5 + off} label="İş Tanımı" hint={editing('details') ? EDIT_HINT : 'Yapılacak işi kısaca açıklayın ve "Kaydet" butonuna basın'} done={hasDesc && !(editing('details') && edit.dirty)} active={activeKey === 'details'} urgent={editing('details')}>
          <div className="space-y-2" onFocusCapture={() => edit.begin('details')}>
          {fix('job_description', <Textarea value={formData.job_description} onChange={(e) => set({ job_description: e.target.value })} placeholder="Yapılacak işin açıklaması..." rows={3} data-testid="wizard-job-description" />)}
          <div className="flex justify-end">
            {editing('details') ? <EditControls fieldKey="details" edit={edit} testId="wizard-job-description" /> : <SaveButton saved={hasDesc} disabled={!formData.job_description?.trim()} onClick={() => setDescSet(true)} testId="wizard-job-description-save" />}
          </div>
          </div>
        </GuidedField>
      )}

      {hasDesc && (
        <>

          <GuidedField id="types" number={6 + off} label="İş İzni Türleri" optional hint="Varsa iş izni türlerini işaretleyin, ardından aşağıdaki oka basın" done={(formData.work_permit_types || []).length > 0} active={activeKey === 'types'}>
            {fix('work_permit_types', <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 p-3 border rounded-lg bg-slate-50">
              {WORK_PERMIT_TYPES.map(type => (
                <div key={type.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`permit-type-${type.id}`}
                    checked={(formData.work_permit_types || []).includes(type.id)}
                    onCheckedChange={(checked) => setFormData(prev => {
                      const current = prev.work_permit_types || [];
                      return { ...prev, work_permit_types: checked ? [...current, type.id] : current.filter(t => t !== type.id) };
                    })}
                    data-testid={`permit-type-${type.id}`}
                  />
                  <label htmlFor={`permit-type-${type.id}`} className="text-sm cursor-pointer">{type.label}</label>
                </div>
              ))}
            </div>)}
          </GuidedField>
          <EquipmentQuestion formData={formData} set={set} kinds={eqKinds} guardRemoveKinds={guardRemoveKinds} />
          {eqDone && <SpecialSituationsQuestion formData={formData} set={set} onDoneChange={setSsDone} />}
        </>
      )}
      <TimeRuleAlert hits={timeCheck?.hits} suggestion={timeCheck?.suggestion} onApply={(sg) => { set(sg); setStartTimeSet(true); setEndTimeSet(true); toast.success('Önerilen tarih ve saatler uygulandı. Lütfen kontrol edin.'); }} />
    </div>
  );
};

export default WizardStep1;
